import { useTranslation } from 'react-i18next';
import type {
  ContentBlock,
  FileRefBlock,
  GenericBlock,
  SkillCallBlock,
  SkillResultBlock,
  TextBlock,
} from '../../types/api';

interface MessageBubbleProps {
  role: 'user' | 'assistant' | 'system';
  blocks: ContentBlock[];
  /** When true, show typing-indicator if no blocks yet. */
  streaming?: boolean;
}

export function MessageBubble({ role, blocks, streaming }: MessageBubbleProps) {
  const initial = role === 'user' ? 'U' : role === 'system' ? 'S' : '';

  return (
    <div className={`message-row ${role}`}>
      <div className={`msg-avatar ${role}`}>
        {role === 'assistant' ? (
          <img src="/helloant_logo_cropped.png" alt="Hello Ant" />
        ) : (
          initial
        )}
      </div>
      <div className="msg-body">
        {streaming && blocks.length === 0 ? (
          <div className={`msg-bubble ${role}`}>
            <TypingIndicator />
          </div>
        ) : (
          blocks.map((block, i) => <BlockRenderer key={i} block={block} role={role} />)
        )}
      </div>
    </div>
  );
}

export function TypingIndicator() {
  return (
    <div className="typing-indicator" aria-label="Assistant is typing">
      <div className="typing-dot" />
      <div className="typing-dot" />
      <div className="typing-dot" />
    </div>
  );
}

function BlockRenderer({ block, role }: { block: ContentBlock; role: string }) {
  switch (block.type) {
    case 'text':
      return <TextBlockView block={block as TextBlock} role={role} />;
    case 'skill_call':
      return <SkillCallView block={block as SkillCallBlock} />;
    case 'skill_result':
      return <SkillResultView block={block as SkillResultBlock} />;
    case 'file_ref':
      return <FileRefView block={block as FileRefBlock} />;
    default:
      return <GenericBlockView block={block as GenericBlock} />;
  }
}

function TextBlockView({ block, role }: { block: TextBlock; role: string }) {
  return <div className={`msg-bubble ${role}`}>{block.text || '\u200b'}</div>;
}

function SkillCallView({ block }: { block: SkillCallBlock }) {
  const { t } = useTranslation();
  return (
    <div className="skill-card">
      <div className="skill-card-label">
        {t('skillCall')} · {block.skill_id}
      </div>
      <div className="skill-card-body">{JSON.stringify(block.args, null, 2)}</div>
    </div>
  );
}

function SkillResultView({ block }: { block: SkillResultBlock }) {
  const { t } = useTranslation();
  return (
    <div className="skill-card">
      <div className="skill-card-label">
        {t('skillResult')} · {block.skill_id}
        {block.is_error ? ' · error' : ''}
      </div>
      <div className="skill-card-body">{JSON.stringify(block.result, null, 2)}</div>
    </div>
  );
}

function FileRefView({ block }: { block: FileRefBlock }) {
  return (
    <div className="skill-card">
      <div className="skill-card-label">file</div>
      <div className="skill-card-body">
        {block.file_id} ({block.mime})
      </div>
    </div>
  );
}

function GenericBlockView({ block }: { block: GenericBlock }) {
  return (
    <div className="skill-card">
      <div className="skill-card-label">{block.type}</div>
      <div className="skill-card-body">{JSON.stringify(block, null, 2)}</div>
    </div>
  );
}
